import React, { FC, useEffect, useState } from "react";
import { useFormContext, useFormState, useWatch } from "react-hook-form";
import { Trash2, Check, AlertTriangle, RefreshCw } from "lucide-react";
import { Button } from "src/components/ui/button";
import { useAzureFileUpload } from "src/components/AssetUploaders/useAzureFileUpload";
import EditMedia from "../EditMedia/EditMedia";
import MultiMediaUpload from "../MultiMediaUpload/MultiMediaUpload";
import get from "lodash.get";
import { useParams } from "react-router-dom";
import capitalize from "lodash.capitalize";

interface Props {
	stepNumber: number;
}

const MultiMediaUploadStep: FC<Props> = ({ stepNumber }) => {
	const { postId } = useParams<{ postId: string }>();
	const { control, setValue } = useFormContext<BannerFormState>();

	const [uuid, setUUID] = useState<string[]>([]);
	const [mediasPreview, setMediasPreview] = useState<Media[]>([]);

	const { done, error, uploading, upload, clear } = useAzureFileUpload(
		(uuidList) => {
			if (!uuidList) return;

			if (Array.isArray(uuidList)) {
				setUUID((prevState) => [...prevState, ...uuidList]);
			}

			if (!Array.isArray(uuidList)) {
				setUUID((prevState) => [...prevState, uuidList]);
			}
		}
	);

	const {
		done: editDone,
		error: editError,
		uploading: editUploading,
		upload: editUpload,
		clear: editClear
	} = useAzureFileUpload(
		() => {
			// skip
		},
		(uuidList, index) => {
			if (!uuidList && !index) return;

			const newIds = uuid.slice();

			if (Array.isArray(uuidList)) {
				newIds[index as number] = uuidList[0] as string;
			}

			if (!Array.isArray(uuidList)) {
				newIds[index as number] = uuidList as string;
			}

			setUUID(newIds);
		}
	);

	const deleteMedia = (fileId: string, index: number) => {
		setUUID(uuid.filter((id) => id !== fileId));

		setMediasPreview(mediasPreview.filter((_, i) => i !== index));

		if (medias?.length === 1) {
			setValue(`steps.${stepNumber - 1}.value`, String([]));
		}
	};

	const deleteAllMedia = () => {
		setUUID([]);
		setMediasPreview([]);
		setValue(`steps.${stepNumber - 1}.value`, "");
		clear();
		editClear();
	};

	const value = useWatch({
		name: `steps.${stepNumber - 1}.value`,
		control
	});

	const preview = useWatch({
		name: `steps.${stepNumber - 1}.preview`,
		control
	});

	const medias = useWatch({
		name: `steps.${stepNumber - 1}.mediasPreview`,
		control
	});

	const sanitizedMedias =
		medias && medias.filter((media) => media.preview && media.type);

	const note = useWatch({
		name: `steps.${stepNumber - 1}.note`,
		control
	});

	useEffect(() => {
		setValue(`steps.${stepNumber - 1}.mediasPreview`, mediasPreview);
	}, [mediasPreview]);

	useEffect(() => {
		const copyUUID = uuid.slice();
		const adaptLocalMediasPreview = mediasPreview.map(
			({ id, preview, file, type }, i) => {
				return {
					id,
					preview,
					file,
					type,
					value: copyUUID[i]
				};
			}
		);

		setValue(`steps.${stepNumber - 1}.mediasPreview`, adaptLocalMediasPreview);
	}, [uuid]);

	useEffect(() => {
		if (!postId || !sanitizedMedias) return;

		const getUUID = sanitizedMedias.map((file) => file.value);

		if (getUUID.length) {
			setUUID([...getUUID]);
			setMediasPreview(sanitizedMedias as Media[]);
		}
	}, [postId, preview]);

	const { errors } = useFormState({ control });

	const isAnswerInvalid =
		Boolean(get(errors, `steps[${stepNumber - 1}]`, null)) ||
		error ||
		editError;

	useEffect(() => {
		setValue(`steps.${stepNumber - 1}.error`, error || editError);
	}, [error, editError]);

	useEffect(() => {
		setValue(`steps.${stepNumber - 1}.uploading`, uploading || editUploading);
	}, [uploading, editUploading]);

	const isThereVideoUploaded =
		sanitizedMedias &&
		sanitizedMedias.some((media) => media.type.includes("video"));

	const isUploaded =
		!error &&
		!editError &&
		!uploading &&
		!editUploading &&
		((done && editDone) || value);

	return (
		<div>
			<div className="flex flex-col items-start justify-between gap-2">
				{medias && !medias.length && (
					<p
						className="text-sm font-medium text-zinc-600"
						aria-label="step-title"
					>
						{note}
					</p>
				)}

				{sanitizedMedias &&
					sanitizedMedias.map(({ file, value, id, type }, index) => {
						return (
							<div
								className="flex items-center justify-between w-full"
								key={id}
							>
								<div className="flex items-center gap-4">
									<p
										className="text-sm"
										style={{
											textOverflow: "ellipsis",
											maxWidth: 250,
											whiteSpace: "nowrap",
											overflow: "hidden"
										}}
									>
										{file ? file : capitalize(type)}
									</p>
									<div className="flex items-center gap-4">
										<EditMedia
											stepNumber={stepNumber}
											control={control}
											index={index}
											setMediasPreview={setMediasPreview}
											mediasPreview={mediasPreview}
											setValue={setValue}
											upload={editUpload}
											error={error || editError}
											uploading={uploading || editUploading}
											isVideo={type.includes("video")}
										/>
										<Button
											type="button"
											variant="ghost"
											size="icon"
											onClick={() => deleteMedia(value, index)}
											disabled={
												uploading || editUploading || error || editError
											}
										>
											<Trash2 size={18} />
										</Button>
									</div>
								</div>
								{isUploaded && (
									<div className="flex items-center gap-1 text-zinc-500">
										<Check size={20} />
										<span>Uploaded</span>
									</div>
								)}

								{(uploading || editUploading) && (
									<div className="flex items-center gap-1 text-zinc-500">
										<div className="animate-spin">
											<RefreshCw size={20} />
										</div>
										<span>Uploading</span>
									</div>
								)}
							</div>
						);
					})}

				{isThereVideoUploaded && (
					<p className="text-zinc-500" aria-label="step-title">
						Only one video can be added to the post
					</p>
				)}

				{(error || editError) && (
					<div className="flex items-center gap-1 text-destructive">
						<AlertTriangle size={18} />
						<span className="text-sm">
							Problem uploading the media(s). Please try again.
						</span>
					</div>
				)}
			</div>

			<div
				className={[
					"flex items-center justify-start gap-8 my-4",
					isAnswerInvalid || error ? "border border-destructive p-2" : ""
				].join(" ")}
			>
				<MultiMediaUpload
					stepNumber={stepNumber}
					control={control}
					error={error || editError}
					upload={(file: File, uploadType: string, uploadIndex?: number) =>
						upload(
							file,
							uploadType,
							uploadIndex,
							"retail-app-social-image-sponsored"
						)
					}
					uploading={(uploading || editUploading) && !done && !editDone}
					uuid={uuid}
					setMediasPreview={setMediasPreview}
					deleteAllMedia={deleteAllMedia}
					setValue={setValue}
				/>
			</div>
		</div>
	);
};

export default MultiMediaUploadStep;
